import React, { useState, useEffect } from "react"
import Table, { PercentageBadge } from "../StarterGraphs/Table"
import ActualScheduledDistribution from "../Graphs/ActualScheduledDistribution"

const TreatmentVarianceModal = ({
	showModal,
	closeModal,
	data, // This is now the raw historicalData
	processedData = [], // This is the pre-processed treatment data
	initialTreatment = null, // Initial treatment to select
	threshold = 20, // Updated threshold to 20%
	selectedRange,
	setSelectedRange
}) => {
	const [selectedTreatment, setSelectedTreatment] = useState(initialTreatment)
	const [, setFilteredData] = useState([])
	const [treatmentData, setTreatmentData] = useState([])
	const [showAllTreatments, setShowAllTreatments] = useState(false)
	const initialDisplayCount = 5 // Show only first 5 treatments initially
	const [debugInfo, setDebugInfo] = useState({
		hasData: false,
		dataLength: 0,
		hasProcessedData: false,
		processedDataLength: 0,
		hasFilteredData: false,
		filteredDataLength: 0,
		hasSelectedTreatment: false,
		selectedTreatmentValue: null
	})

	// Handle escape key to close modal
	useEffect(() => {
		const handleEscape = event => {
			if (event.key === "Escape") {
				closeModal()
			}
		}
		document.addEventListener("keydown", handleEscape)
		return () => document.removeEventListener("keydown", handleEscape)
	}, [closeModal])

	// Filter data by selected range and prepare treatment data
	useEffect(() => {
		// Update debug info
		setDebugInfo(prev => ({
			...prev,
			hasData: Boolean(data && data.length),
			dataLength: data?.length || 0,
			hasProcessedData: Boolean(processedData && processedData.length),
			processedDataLength: processedData?.length || 0
		}))

		if (!data || data.length === 0) return

		// Reset show all flag when data changes
		setShowAllTreatments(false)

		// Filter data by range
		const filtered = filterDataByRange(selectedRange, data)

		// Format the filtered data for ActualScheduledDistribution if needed
		// The ActualScheduledDistribution component expects data with treatment_duration_variance.treatments
		const formattedData = filtered.map(day => {
			// Check if the data already has the expected format
			if (day.treatment_duration_variance?.treatments) {
				return day
			}

			// If not, try to create the expected format from processedData
			return {
				...day,
				treatment_duration_variance: {
					treatments: processedData.map(treatment => ({
						appointmentType: treatment.appointmentType || treatment.name,
						name: treatment.name,
						scheduledDuration: treatment.scheduledDuration,
						actualDurations: treatment.actualDurations || []
					}))
				}
			}
		})

		setFilteredData(formattedData)

		// Update debug info
		setDebugInfo(prev => ({
			...prev,
			hasFilteredData: Boolean(formattedData && formattedData.length),
			filteredDataLength: formattedData?.length || 0
		}))

		// Check if we have pre-processed data
		if (processedData && processedData.length > 0) {
			// Sort the processed data by variance magnitude
			const sortedData = [...processedData]
				.filter(treatment => {
					const sampleSize =
						treatment.totalAppointments || treatment.actualDurations?.length || 0
					return sampleSize > 15 // Only include treatments with sample size > 15
				})
				.sort(
					(a, b) =>
						Math.abs(
							((b.actualDuration - b.scheduledDuration) / b.scheduledDuration) * 100
						) -
						Math.abs(
							((a.actualDuration - a.scheduledDuration) / a.scheduledDuration) * 100
						)
				)

			// Add sample size and percentage difference if not already present
			const enhancedData = sortedData.map(treatment => {
				if (!treatment.percentDifference) {
					const percentDiff =
						treatment.scheduledDuration > 0
							? (
									((treatment.actualDuration - treatment.scheduledDuration) /
										treatment.scheduledDuration) *
									100
								).toFixed(1)
							: 0

					return {
						...treatment,
						recommendation: Math.ceil(treatment.actualDuration / 10) * 10,
						percentDifference: parseFloat(percentDiff),
						sampleSize:
							treatment.totalAppointments || treatment.actualDurations?.length || 0
					}
				}
				return {
					...treatment,
					sampleSize:
						treatment.totalAppointments || treatment.actualDurations?.length || 0
				}
			})

			setTreatmentData(enhancedData)

			// Set initial selected treatment if not already set
			if (!selectedTreatment && enhancedData.length > 0) {
				const treatmentToSelect = enhancedData[0].appointmentType || enhancedData[0].name
				setSelectedTreatment(treatmentToSelect)

				// Update debug info
				setDebugInfo(prev => ({
					...prev,
					hasSelectedTreatment: Boolean(treatmentToSelect),
					selectedTreatmentValue: treatmentToSelect
				}))
			} else {
				// Update debug info for existing selected treatment
				setDebugInfo(prev => ({
					...prev,
					hasSelectedTreatment: Boolean(selectedTreatment),
					selectedTreatmentValue: selectedTreatment
				}))
			}

			return
		}

		// If no pre-processed data, process from raw data
		// Prepare treatment variance data
		let allTreatments = []
		filtered.forEach(day => {
			if (day.treatment_duration_variance?.treatments) {
				day.treatment_duration_variance.treatments.forEach(treatment => {
					// Check if treatment is already in the array
					const existingIndex = allTreatments.findIndex(
						t => t.appointmentType === treatment.appointmentType
					)

					if (existingIndex >= 0) {
						// Update existing record by adding actual durations
						if (treatment.actualDurations) {
							allTreatments[existingIndex].actualDurations = [
								...allTreatments[existingIndex].actualDurations,
								...(treatment.actualDurations || [])
							]
						}
					} else {
						// Add new treatment
						allTreatments.push({
							...treatment,
							actualDurations: treatment.actualDurations || []
						})
					}
				})
			}
		})

		// Calculate average actual duration and variance for each treatment
		allTreatments = allTreatments.map(treatment => {
			const actualDurations = treatment.actualDurations || []
			const averageActual =
				actualDurations.length > 0
					? actualDurations.reduce((sum, d) => sum + d.duration, 0) /
						actualDurations.length
					: 0

			const percentDifference =
				treatment.scheduledDuration > 0
					? ((averageActual - treatment.scheduledDuration) /
							treatment.scheduledDuration) *
						100
					: 0

			return {
				...treatment,
				actualDuration: Math.round(averageActual),
				recommendation: Math.ceil(Math.round(averageActual) / 10) * 10,
				percentDifference: parseFloat(percentDifference.toFixed(1)),
				sampleSize: actualDurations.length,
				name: treatment.name || treatment.appointmentType
			}
		})

		// Filter treatments with sufficient sample size
		allTreatments = allTreatments.filter(treatment => treatment.sampleSize > 15)

		// Sort by absolute variance
		allTreatments.sort((a, b) => Math.abs(b.percentDifference) - Math.abs(a.percentDifference))

		setTreatmentData(allTreatments)

		// Set default selected treatment (the one with the highest variance)
		if (allTreatments.length > 0 && !selectedTreatment) {
			const treatmentToSelect = initialTreatment || allTreatments[0].appointmentType
			setSelectedTreatment(treatmentToSelect)

			// Update debug info
			setDebugInfo(prev => ({
				...prev,
				hasSelectedTreatment: Boolean(treatmentToSelect),
				selectedTreatmentValue: treatmentToSelect
			}))
		} else {
			// Update debug info for existing selected treatment
			setDebugInfo(prev => ({
				...prev,
				hasSelectedTreatment: Boolean(selectedTreatment),
				selectedTreatmentValue: selectedTreatment
			}))
		}
	}, [data, selectedRange, processedData, initialTreatment, selectedTreatment])

	// Handle treatment selection from the table
	const handleTreatmentSelect = treatment => {
		const treatmentId = treatment.appointmentType || treatment.name
		setSelectedTreatment(treatmentId)

		// Update debug info
		setDebugInfo(prev => ({
			...prev,
			hasSelectedTreatment: Boolean(treatmentId),
			selectedTreatmentValue: treatmentId
		}))
	}

	const filterDataByRange = (range, data) => {
		let filtered

		switch (range) {
			case "7days":
				filtered = data.slice(-7)
				break
			case "1month":
				filtered = data.slice(-30)
				break
			case "6months":
				filtered = data.slice(-180)
				break
			default:
				filtered = data
		}
		return filtered
	}

	// Define table columns
	const columns = [
		{ key: "name", label: "Treatment" },
		{
			key: "scheduledDuration",
			label: "Scheduled (min)",
			isNumeric: true
		},
		{
			key: "actualDuration",
			label: "Actual (min)",
			isNumeric: true
		},
		{
			key: "recommendation",
			label: "Recommendation (min)",
			isNumeric: true
		},
		{
			key: "percentDifference",
			label: "% Difference",
			isNumeric: true,
			render: item => (
				<PercentageBadge value={item.percentDifference} isHigherBetter={false} />
			)
		},
		{
			key: "sampleSize",
			label: "Sample Size",
			isNumeric: true
		}
	]

	if (!showModal) return null

	// Get the data to display (either all treatments or just the first 5)
	const displayedTreatmentData = showAllTreatments
		? treatmentData
		: treatmentData.slice(0, initialDisplayCount)

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
			<div className="bg-white rounded-lg shadow-lg w-full max-w-6xl mx-auto max-h-[90vh] flex flex-col">
				<div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
					<h3 className="text-lg font-medium text-gray-900">
						Treatment Duration Analysis
					</h3>
					<button
						onClick={closeModal}
						className="text-gray-400 hover:text-gray-600 focus:outline-none"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				<div className="p-8 overflow-auto flex-1">
					{/* Stacked layout - Table first, then graph */}
					<div className="flex flex-col space-y-8">
						{/* Table section */}
						<div className="w-full">
							<div
								className="overflow-auto border border-gray-200 rounded-lg"
								style={{ maxHeight: "300px" }}
							>
								<Table
									data={displayedTreatmentData}
									columns={columns}
									height="auto"
									title={null} // Remove title as we've added it above
									onRowClick={handleTreatmentSelect}
									emptyMessage="No treatment data available"
								/>
							</div>

							{/* Show more treatments button */}
							{treatmentData.length > initialDisplayCount && (
								<div className="mt-3 text-right">
									<button
										onClick={() => setShowAllTreatments(!showAllTreatments)}
										className="inline-flex items-center px-4 py-2 border border-customBlue-400 text-sm font-medium rounded-md text-customBlue-700 bg-white hover:bg-customBlue-50"
									>
										{showAllTreatments
											? "Show Less"
											: `Show All Treatments (${treatmentData.length})`}
									</button>
								</div>
							)}
						</div>

						{/* Graph section */}
						<div className="w-full">
							<div className="h-[400px] border border-gray-200 rounded-lg">
								{/* Debug info if treatments aren't showing */}
								{debugInfo.filteredDataLength > 0 && treatmentData.length > 0 ? (
									<ActualScheduledDistribution
										data={data}
										height={400}
										chartHeight={350}
										initialTreatment={selectedTreatment}
									/>
								) : (
									<div className="flex flex-col h-full items-center justify-center p-4 bg-gray-50 rounded-lg">
										<p className="text-gray-500 mb-4">
											No treatment data available with sufficient sample size
										</p>
										<div className="text-xs text-left text-gray-400 p-2 bg-gray-100 rounded w-full max-w-md">
											<p>Debug Info:</p>
											<ul className="list-disc pl-5 mt-1">
												<li>
													Has Data: {debugInfo.hasData ? "Yes" : "No"} (
													{debugInfo.dataLength} items)
												</li>
												<li>
													Has Processed Data:{" "}
													{debugInfo.hasProcessedData ? "Yes" : "No"} (
													{debugInfo.processedDataLength} items)
												</li>
												<li>
													Has Filtered Data:{" "}
													{debugInfo.hasFilteredData ? "Yes" : "No"} (
													{debugInfo.filteredDataLength} items)
												</li>
												<li>
													Has Selected Treatment:{" "}
													{debugInfo.hasSelectedTreatment ? "Yes" : "No"}{" "}
													({debugInfo.selectedTreatmentValue})
												</li>
												<li>
													Treatment Data Length: {treatmentData.length}
												</li>
											</ul>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default TreatmentVarianceModal
